/**
 * Hermes — Consultar producto
 *
 * Botón + modal de consulta para productos de ticket alto (acordeones sin precio
 * público). La consulta se envía a la app de loyalty vía App Proxy, con el mismo
 * contrato que `loyalty-helper.js`: POST mismo-origen a `/apps/loyalty` con un
 * discriminador `_action` en el body. Shopify firma la petición y agrega
 * `logged_in_customer_id`, así que no hay CORS ni bearer token.
 *
 * Contrato (ver docs/loyalty-architecture.md):
 *   POST /apps/loyalty
 *   { _action: 'product_inquiry', producto: {...}, contacto: {...} }
 *   -> 200 { success: true, folio?: string }
 *
 * Datos del producto: Shopify no propaga `closest.product` a bloques custom vía
 * `content_for 'blocks'`, solo a bloques stock de Horizon. El elemento lee los
 * datos desde el `<product-card>` padre (que sí los tiene del server-side render)
 * y desde el link de accesibilidad que incluye título y URL. Si no encuentra la
 * card padre o el ID está vacío (estado placeholder), se oculta.
 */

const API_URL = '/apps/loyalty';

class HermesConsultar extends HTMLElement {
  connectedCallback() {
    if (!this._resolveProductData()) return;

    this.trigger = this.querySelector('[data-consultar-trigger]');
    this.dialog = this.querySelector('[data-consultar-dialog]');
    this.form = this.querySelector('[data-consultar-form]');
    this.status = this.querySelector('[data-consultar-status]');
    this.submitButton = this.querySelector('[data-consultar-submit]');

    if (!this.trigger || !this.dialog || !this.form) return;

    this.submitLabel = this.submitButton?.textContent.trim() ?? '';

    this.trigger.addEventListener('click', this.open);
    this.form.addEventListener('submit', this.submit);

    this.querySelectorAll('[data-consultar-close]').forEach((button) => {
      button.addEventListener('click', this.close);
    });

    // Clic en el backdrop cierra el modal. Clic dentro del panel detiene la
    // propagación para que el listener de product-card no interprete clics en
    // textarea, spans u otros elementos no-interactivos como navegación.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) {
        this.close();
      } else {
        event.stopPropagation();
      }
    });

    // El foco en inputs ocurre en mousedown, no en click. Si algo llama
    // preventDefault en mousedown (ej. product-card.js) el input no recibe foco
    // aunque el click llegue. Detener propagación aquí previene interferencia.
    this.dialog.addEventListener('mousedown', (event) => {
      if (event.target !== this.dialog) event.stopPropagation();
    });
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.open);
    this.form?.removeEventListener('submit', this.submit);
  }

  /**
   * Resuelve los datos del producto. Prioridad:
   *  1. Atributos ya en el elemento (caso: block.settings.product asignado en Liquid).
   *  2. DOM del <product-card> padre (caso: bloque dentro de product-list).
   *
   * Retorna true si hay datos válidos, false si se debe ocultar el elemento.
   */
  _resolveProductData() {
    // Caso 1: el ajuste `product` del schema llenó los atributos vía Liquid.
    if (this.dataset.productId) return true;

    // Caso 2: leer desde el <product-card> padre (contexto product-list).
    const card = this.closest('product-card');
    const productId = card?.dataset.productId;

    if (!productId) {
      // No hay producto real (card placeholder o uso fuera de tarjeta sin ajuste).
      this.hidden = true;
      return false;
    }

    // El <a class="product-card__link"> tiene el href y el <span.visually-hidden> con el título.
    const link = card.querySelector('a.product-card__link');
    const productUrl = link?.href ?? '';
    const productTitle = link?.querySelector('.visually-hidden')?.textContent?.trim() ?? '';

    // Derivar el handle de la URL: /products/{handle}?variant=...
    let productHandle = '';
    try {
      const pathname = new URL(productUrl, window.location.origin).pathname;
      const match = pathname.match(/\/products\/([^/?#]+)/);
      if (match) productHandle = match[1];
    } catch {
      // URL inválida — handle queda vacío; la consulta se envía con ID solamente.
    }

    this.dataset.productId = productId;
    this.dataset.productHandle = productHandle;
    this.dataset.productTitle = productTitle;
    this.dataset.productUrl = productUrl;

    // Actualizar el título del producto en el modal.
    const productEl = this.querySelector('[data-consultar-product]');
    if (productEl) productEl.textContent = productTitle;

    return true;
  }

  open = (event) => {
    // La card entera es un link: evitar navegar al abrir el modal.
    event.preventDefault();
    event.stopPropagation();

    this.reset();
    this.dialog.showModal();
  };

  close = () => {
    this.dialog.close();
  };

  reset() {
    this.form.hidden = false;
    this.status.hidden = true;
    this.status.textContent = '';
    this.status.removeAttribute('data-state');
  }

  submit = async (event) => {
    event.preventDefault();

    const formData = new FormData(this.form);
    const payload = {
      _action: 'product_inquiry',
      producto: {
        id: this.dataset.productId,
        handle: this.dataset.productHandle,
        titulo: this.dataset.productTitle,
        url: new URL(this.dataset.productUrl, window.location.origin).href,
      },
      contacto: {
        nombre: formData.get('nombre'),
        email: formData.get('email'),
        telefono: formData.get('telefono'),
        mensaje: formData.get('mensaje'),
      },
    };

    this.setLoading(true);

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });

      // Un 404/501 aquí significa que la app todavía no implementa
      // `product_inquiry`. Se reporta como error, no como éxito.
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'unsuccessful_response');

      this.showSuccess();
    } catch (error) {
      console.error('[HermesConsultar] Error enviando la consulta:', error);
      this.showError();
    } finally {
      this.setLoading(false);
    }
  };

  setLoading(loading) {
    if (!this.submitButton) return;
    this.submitButton.disabled = loading;
    this.submitButton.textContent = loading
      ? this.dataset.sendingText || 'Enviando…'
      : this.submitLabel;
  }

  showSuccess() {
    this.form.hidden = true;
    this.status.hidden = false;
    this.status.dataset.state = 'success';
    this.status.textContent = this.dataset.successText || 'Gracias, recibimos tu consulta. Te contactamos pronto.';
  }

  showError() {
    // El formulario sigue visible con los datos escritos para poder reintentar.
    this.status.hidden = false;
    this.status.dataset.state = 'error';
    this.status.textContent =
      this.dataset.errorText || 'No pudimos enviar tu consulta. Inténtalo de nuevo o llámanos.';
  }
}

if (!customElements.get('hermes-consultar')) {
  customElements.define('hermes-consultar', HermesConsultar);
}
